import { useState, useCallback } from "react";
import { toast } from "react-toastify";
import apiClient from "utils/apiClient";
import { PAYMENT_SERVICE_BASE_URL } from "constants";
import dayjs from "dayjs";

const BASE_URL = `${PAYMENT_SERVICE_BASE_URL}/api/v1/payments`;

export default function usePayouts() {
  const authToken = sessionStorage.getItem("authToken");

  const [payouts, setPayouts] = useState([]);
  const [payoutMeta, setPayoutMeta] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });

  const [charges, setCharges] = useState([]);
  const [chargesMeta, setChargesMeta] = useState({
    page: 0,
    size: 20,
    totalElements: 0,
    totalPages: 0,
  });

  const [externalAccounts, setExternalAccounts] = useState([]);
  const [earningCharges, setEarningCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleAxiosErrors = (err, action = "operation") => {
    let message = "An unexpected error occurred.";

    if (err.response) {
      message =
        err.response.data?.message ||
        `Failed to ${action}: ${err.response.statusText}`;
    } else if (err.request) {
      message =
        "No response from the server. Please check your internet connection.";
    } else {
      message = err.message;
    }

    toast.error(message);
    console.error(`${action} error`, err);
  };

  // fetch user payout
  const fetchUserPayouts = useCallback(
    async (userId, page = 0, size = 20) => {
      if (!authToken || !userId) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(`${BASE_URL}/payout/user/${userId}`, {
          params: { page, size },
        });

        const { content = [], ...meta } = res.data || {};

        setPayouts(content);
        setPayoutMeta(meta);

        return res.data;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch payouts");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // user sent payouts
  const fetchSentPayouts = useCallback(
    async (userId, page = 0, size = 20) => {
      if (!authToken || !userId) return;
      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(
          `${BASE_URL}/payout/user/${userId}/sent`,
          {
            params: { page, size },
          },
        );

        const { content = [], ...meta } = res.data || {};

        setPayouts(content);
        setPayoutMeta(meta);

        return res.data;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch sent payouts");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // user received payouts
  const fetchReceivedPayouts = useCallback(
    async (userId, page = 0, size = 20) => {
      if (!authToken || !userId) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(
          `${BASE_URL}/payout/user/${userId}/received`,
          {
            params: { page, size },
          },
        );

        const { content = [], ...meta } = res.data || {};

        setPayouts(content);
        setPayoutMeta(meta);

        return res.data;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch received payouts");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // fetch all or user-specific payout charges
  const fetchCharges = useCallback(
    async ({ start, end, page = 0, size = 20, userId = null }) => {
      if (!authToken) return;
      const startDate =
        start || dayjs().subtract(10, "years").startOf("day").toISOString();
      const endDate = end || dayjs().endOf("day").toISOString();

      setLoading(true);
      setError(null);

      try {
        const url = userId
          ? `${BASE_URL}/charges/user/range`
          : `${BASE_URL}/charges`;
        const params = userId
          ? { uid: userId, start:startDate, end:endDate, page, size }
          : { start:startDate, end:endDate, page, size };

        const res = await apiClient.get(url, { params });
        const { content = [], ...meta } = res.data || {};
        setCharges(content);
        setChargesMeta(meta);

        return res.data;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, userId ? "fetch user charges" : "fetch charges");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // fetch charges for a specific user within a date range
  const fetchUserChargesInRange = useCallback(
    async (userId, start, end, page = 0, size = 20) => {
      if (!authToken || !userId || !start || !end) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(`${BASE_URL}/charges/user/range`, {
          params: { uid: userId, start, end, page, size },
        });

        const { content = [], ...meta } = res.data || {};
        setCharges(content);
        setChargesMeta(meta);

        return res.data;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch user charges");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // fetch external accounts
  const fetchExternalAccounts = useCallback(
    async (uid) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(`${BASE_URL}/external/accounts`, {
          params: { uid },
        });

        const accounts = Array.isArray(res.data) ? res.data : [];
        setExternalAccounts(accounts);

        return accounts;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch external accounts");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // fetch active external accounts
  const fetchActiveExternalAccounts = useCallback(
    async (uid) => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(
          `${BASE_URL}/external/accounts/active`,
          {
            params: { uid },
          },
        );

        const accounts = Array.isArray(res.data) ? res.data : [];
        setExternalAccounts(accounts);
        return accounts;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch active external accounts");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // earning/charges
  const fetchEarningCharges = useCallback(
    async (uid, status = "AVAILABLE") => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const res = await apiClient.get(`${BASE_URL}/earnings/charges`, {
          params: { uid, status },
        });

        const charges = Array.isArray(res.data) ? res.data : [];
        setEarningCharges(charges);

        return charges;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "fetch earning charges");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  // earnings/cashout
  const cashOutEarnings = useCallback(
    async (chargeIds, currency = "usd") => {
      if (!authToken) return;

      setLoading(true);
      setError(null);

      try {
        const payload = {
          chargeIds,
          currency,
        };

        const res = await apiClient.post(
          `${BASE_URL}/earnings/cashout`,
          payload,
        );

        toast.success("Cashout successful");

        return res.data;
      } catch (err) {
        setError(err);
        handleAxiosErrors(err, "cash out earnings");
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  return {
    loading,
    error,

    payouts,
    setPayouts,
    payoutMeta,
    charges,
    chargesMeta,

    externalAccounts,
    earningCharges,

    fetchUserPayouts,
    fetchSentPayouts,
    fetchReceivedPayouts,

    fetchCharges,
    fetchUserChargesInRange,

    fetchExternalAccounts,
    fetchActiveExternalAccounts,
    fetchEarningCharges,
    cashOutEarnings,
  };
}
